import { t, type Locale } from './i18n';
import type { Frequency } from './push';

export type ViewState =
  | { kind: 'checking' }
  | { kind: 'idle'; frequency: Frequency }
  | { kind: 'enabling' }
  | { kind: 'active'; frequency: Frequency; pending: Frequency; saving: boolean }
  | { kind: 'disabling' }
  | { kind: 'disabled' }
  | { kind: 'denied' }
  | { kind: 'unsupported' }
  | { kind: 'ios-install-needed' }
  | { kind: 'error' };

export interface Handlers {
  onSelectFrequency(freq: Frequency): void;
  onEnable(): void;
  onSaveFrequency(): void;
  onDisable(): void;
  onEnableAgain(): void;
  onRetry(): void;
}

const FREQUENCIES: { value: Frequency; titleKey: 'freqOccasionallyTitle' | 'freqRegularlyTitle' | 'freqFrequentlyTitle' | 'freqRegretTitle'; subKey: 'freqOccasionallySub' | 'freqRegularlySub' | 'freqFrequentlySub' | 'freqRegretSub' }[] = [
  { value: 'occasionally', titleKey: 'freqOccasionallyTitle', subKey: 'freqOccasionallySub' },
  { value: 'regularly', titleKey: 'freqRegularlyTitle', subKey: 'freqRegularlySub' },
  { value: 'frequently', titleKey: 'freqFrequentlyTitle', subKey: 'freqFrequentlySub' },
  { value: 'regret', titleKey: 'freqRegretTitle', subKey: 'freqRegretSub' },
];

export function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

function freqGrid(selected: Frequency, locale: Locale, name: string): string {
  return `<div class="freq-grid" role="radiogroup" aria-label="${esc(t('howOften', locale))}">
    ${FREQUENCIES.map(
      (f) => `<div class="freq-card">
        <input type="radio" name="${name}" id="${name}-${f.value}" value="${f.value}" ${f.value === selected ? 'checked' : ''} />
        <label for="${name}-${f.value}">
          <span class="freq-title">${esc(t(f.titleKey, locale))}</span>
          <span class="freq-sub">${esc(t(f.subKey, locale))}</span>
        </label>
      </div>`,
    ).join('')}
  </div>`;
}

/**
 * Headline + subcopy. Also present as static HTML in index.html so crawlers
 * (and visitors whose browser can't do push) always see what the site is.
 */
function intro(locale: Locale): string {
  return `<h1 class="headline">${esc(t('headline', locale))}</h1>
        <p class="subcopy">${esc(t('subcopy', locale))}</p>`;
}

export function render(container: HTMLElement, state: ViewState, locale: Locale, handlers: Handlers): void {
  switch (state.kind) {
    case 'checking':
      container.innerHTML = `<div class="hero-inner" aria-live="polite">
        ${intro(locale)}
      </div>`;
      return;

    case 'idle':
      container.innerHTML = `<div class="hero-inner">
        ${intro(locale)}
        <div class="freq-label">${esc(t('howOften', locale))}</div>
        ${freqGrid(state.frequency, locale, 'freq')}
        <div class="btn-row">
          <button type="button" class="btn btn-primary" data-action="enable">
            ${esc(t('enableButton', locale))} <span aria-hidden="true">→</span>
          </button>
          <p class="caption">${esc(t('enableCaption', locale))}</p>
        </div>
      </div>`;
      wireFrequencyInputs(container, handlers);
      container.querySelector('[data-action="enable"]')?.addEventListener('click', handlers.onEnable);
      return;

    case 'enabling':
      container.innerHTML = `<div class="hero-inner" aria-live="polite">
        ${intro(locale)}
        <div class="btn-row">
          <button type="button" class="btn btn-primary" disabled>${esc(t('enablingButton', locale))}</button>
        </div>
      </div>`;
      return;

    case 'active':
      container.innerHTML = `<div class="hero-inner">
        <p class="state-message">${esc(t('activeMessage', locale))}</p>
        ${freqGrid(state.pending, locale, 'freq-active')}
        <div class="btn-row">
          <button type="button" class="btn btn-primary" data-action="save" ${state.saving || state.pending === state.frequency ? 'disabled' : ''}>
            ${esc(t(state.saving ? 'savingButton' : 'saveFrequencyButton', locale))}
          </button>
          <button type="button" class="btn btn-text" data-action="disable">${esc(t('disableButton', locale))}</button>
        </div>
      </div>`;
      wireFrequencyInputs(container, handlers);
      container.querySelector('[data-action="save"]')?.addEventListener('click', handlers.onSaveFrequency);
      container.querySelector('[data-action="disable"]')?.addEventListener('click', handlers.onDisable);
      return;

    case 'disabling':
      container.innerHTML = `<div class="hero-inner" aria-live="polite">
        <p class="state-message">${esc(t('activeMessage', locale))}</p>
        <div class="btn-row">
          <button type="button" class="btn btn-text" disabled>${esc(t('disablingButton', locale))}</button>
        </div>
      </div>`;
      return;

    case 'disabled':
      container.innerHTML = `<div class="hero-inner">
        <p class="state-message">${esc(t('disabledMessage', locale))}</p>
        <div class="btn-row">
          <button type="button" class="btn btn-secondary" data-action="enable-again">${esc(t('enableAgainButton', locale))}</button>
        </div>
      </div>`;
      container.querySelector('[data-action="enable-again"]')?.addEventListener('click', handlers.onEnableAgain);
      return;

    case 'denied':
      container.innerHTML = `<div class="hero-inner">
        ${intro(locale)}
        <div class="state-icon" aria-hidden="true">🔕</div>
        <p class="state-message">${esc(t('deniedMessage', locale))}</p>
        <p class="state-note">${esc(t('deniedNote', locale))}</p>
      </div>`;
      return;

    case 'unsupported':
      container.innerHTML = `<div class="hero-inner">
        ${intro(locale)}
        <div class="state-icon" aria-hidden="true">🔕</div>
        <p class="state-message">${esc(t('unsupportedMessage', locale))}</p>
        <p class="state-note">${esc(t('unsupportedNote', locale))}</p>
      </div>`;
      return;

    case 'ios-install-needed':
      container.innerHTML = `<div class="hero-inner">
        ${intro(locale)}
        <div class="state-icon" aria-hidden="true">📲</div>
        <p class="state-message">${esc(t('iosInstallMessage', locale))}</p>
        <ol class="ios-install-steps">
          <li>
            <svg class="ios-share-icon" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 3v12" /><path d="M8 7l4-4 4 4" /><path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
            </svg>
            <span>${esc(t('iosInstallStep1', locale))}</span>
          </li>
          <li><span>${esc(t('iosInstallStep2', locale))}</span></li>
        </ol>
      </div>`;
      return;

    case 'error':
      container.innerHTML = `<div class="hero-inner">
        <p class="state-message">${esc(t('errorMessage', locale))}</p>
        <div class="btn-row">
          <button type="button" class="btn btn-secondary" data-action="retry">${esc(t('tryAgainButton', locale))}</button>
        </div>
      </div>`;
      container.querySelector('[data-action="retry"]')?.addEventListener('click', handlers.onRetry);
      return;
  }
}

function wireFrequencyInputs(container: HTMLElement, handlers: Handlers): void {
  container.querySelectorAll<HTMLInputElement>('input[type="radio"][name^="freq"]').forEach((input) => {
    input.addEventListener('change', () => {
      if (input.checked) handlers.onSelectFrequency(input.value as Frequency);
    });
  });
}
